import React, { useState } from 'react';
import {
  ChevronRight,
  Folder,
  File,
  Upload,
  Download,
  MoreVertical,
  ArrowLeftIcon as Back,
  Lock,
} from 'lucide-react';
import { Window } from '../../ui/Window';
import { DragPortal } from '../../ui/DragPortal';
import { PasswordDialog } from '../../ui/PasswordDialog';
import { TimeLine } from './TimeLine';
import { Notice } from './Notice';
import { Oncall } from './Oncall';
import { Eval } from './Eval';
import { useFileStore, FileItem } from '../../../stores/fileStore';
import { playSound } from '../../../utils/soundService';

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface OpenedFileWindow {
  instanceId: string;
  file: FileItem;
}

export function FileApp() {
  const currentPath = useFileStore((state) => state.currentPath);
  const selectedFile = useFileStore((state) => state.selectedFile);
  const files = useFileStore((state) => state.files);
  const setCurrentPath = useFileStore((state) => state.setCurrentPath);
  const setSelectedFile = useFileStore((state) => state.setSelectedFile);
  const getFilesForPath = useFileStore((state) => state.getFilesForPath);
  const unlockFile = useFileStore((state) => state.unlockFile);

  const [openedFiles, setOpenedFiles] = useState<OpenedFileWindow[]>([]);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<FileItem | null>(null);
  const [passwordError, setPasswordError] = useState(false);
  const [focusedWindowId, setFocusedWindowId] = useState<string | null>(null);

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [{ name: 'root', path: 'root' }];
    if (currentPath !== 'root') {
      const currentFile = files.find((f) => f.id === currentPath);
      if (currentFile) {
        breadcrumbs.push({ name: currentFile.name, path: currentPath });
      }
    }
    return breadcrumbs;
  };

  const handleFolderClick = (file: FileItem) => {
    if (file.protected) {
      setPendingFile(file);
      setPasswordDialogOpen(true);
      setPasswordError(false);
      playSound('sidebar-locked');
    } else {
      playSound('open');
      setCurrentPath(file.id);
    }
  };

  const handleNavigateBack = () => {
    if (currentPath !== 'root') {
      playSound('click');
      setCurrentPath('root');
    }
  };

  const handleFileRowKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    file: FileItem,
  ) => {
    if (e.key === 'Enter') {
      if (file.type === 'folder') {
        handleFolderClick(file);
      } else {
        setSelectedFile(file);
      }
    } else if (e.key === ' ') {
      e.preventDefault();
      setSelectedFile(file);
    }
  };

  const handleFileDoubleClick = (file: FileItem) => {
    if (file.type === 'file') {
      if (file.protected) {
        setPendingFile(file);
        setPasswordDialogOpen(true);
        setPasswordError(false);
        playSound('sidebar-locked');
      } else {
        playSound('open');
        const instanceId = `${file.id}-${Date.now()}`;
        setOpenedFiles([...openedFiles, { instanceId, file }]);
      }
    } else if (file.type === 'folder') {
      handleFolderClick(file);
    }
  };

  const handleCloseFile = (instanceId: string) => {
    setOpenedFiles(openedFiles.filter((w) => w.instanceId !== instanceId));
  };

  const handlePasswordSubmit = (password: string) => {
    if (password === pendingFile?.password) {
      playSound('success');
      setPasswordError(false);
      setPasswordDialogOpen(false);
      if (pendingFile) {
        // Unlock the file
        unlockFile(pendingFile.id);

        if (pendingFile.type === 'file') {
          const instanceId = `${pendingFile.id}-${Date.now()}`;
          setOpenedFiles([...openedFiles, { instanceId, file: pendingFile }]);
        } else {
          setCurrentPath(pendingFile.id);
        }
        setPendingFile(null);
      }
    } else {
      playSound('password-fail');
      setPasswordError(true);
    }
  };

  const getFileComponent = (fileId: string) => {
    switch (fileId) {
      case '3':
        return <TimeLine />;
      case '4':
        return <Notice />;
      case '5':
        return <Oncall />;
      case '6':
        return <Eval />;
      default:
        return null;
    }
  };

  const currentFiles = getFilesForPath(currentPath);
  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="h-full flex flex-col bg-white relative">
      <DragPortal />
      {/* Top bar with breadcrumbs and actions */}
      <div className="border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {currentPath !== 'root' && (
              <button
                onClick={handleNavigateBack}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Back"
              >
                <Back size={18} strokeWidth={1.5} className="text-gray-600" />
              </button>
            )}
            <div className="flex items-center gap-1">
              {breadcrumbs.map((breadcrumb, index) => (
                <div key={breadcrumb.path} className="flex items-center gap-1">
                  {index > 0 && (
                    <ChevronRight
                      size={16}
                      strokeWidth={1.5}
                      className="text-gray-400"
                    />
                  )}
                  <button
                    onClick={() => {
                      playSound('click');
                      setCurrentPath(breadcrumb.path);
                    }}
                    className="text-sm text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    {breadcrumb.name}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Upload"
            >
              <Upload size={18} strokeWidth={1.5} className="text-gray-600" />
            </button>
            <button
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Download"
            >
              <Download size={18} strokeWidth={1.5} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* File list */}
      <div className="flex-1 overflow-y-auto">
        <div className="divide-y divide-gray-200">
          {/* Header row */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-4 gap-4 text-xs font-medium text-gray-600 uppercase tracking-wider">
              <div>Name</div>
              <div>Size</div>
              <div>Modified</div>
              <div />
            </div>
          </div>

          {/* File rows */}
          {currentFiles.map((file) => (
            <div
              key={file.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedFile(file);
              }}
              onDoubleClick={() => handleFileDoubleClick(file)}
              onKeyDown={(e) => handleFileRowKeyDown(e, file)}
              className={`px-6 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-200 last:border-b-0 select-none ${
                selectedFile?.id === file.id ? 'bg-gray-100' : ''
              }`}
            >
              <div className="grid grid-cols-4 gap-4 items-center">
                <div className="flex items-center gap-2 min-w-0">
                  {file.type === 'folder' ? (
                    <Folder
                      size={16}
                      strokeWidth={1.5}
                      className="text-gray-600 flex-shrink-0"
                    />
                  ) : (
                    <File
                      size={16}
                      strokeWidth={1.5}
                      className="text-gray-600 flex-shrink-0"
                    />
                  )}
                  <span className="text-sm text-gray-900 truncate">
                    {file.name}
                  </span>
                  {file.protected && (
                    <Lock
                      size={14}
                      strokeWidth={1.5}
                      className="text-gray-600 flex-shrink-0"
                    />
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {file.size ? file.size : '—'}
                </div>
                <div className="text-sm text-gray-600">{file.modified}</div>
                <div className="flex justify-end">
                  <button className="p-1 hover:bg-gray-200 rounded transition-colors opacity-0 hover:opacity-100">
                    <MoreVertical
                      size={16}
                      strokeWidth={1.5}
                      className="text-gray-600"
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* File details footer */}
      {selectedFile && (
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="grid grid-cols-4 gap-8">
            <div>
              <p className="text-xs text-gray-600 font-medium">Name</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-gray-900">{selectedFile.name}</p>
                {selectedFile.protected && <Lock size={14} />}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Type</p>
              <p className="text-sm text-gray-900 mt-1 capitalize">
                {selectedFile.type}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Size</p>
              <p className="text-sm text-gray-900 mt-1">
                {selectedFile.size || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Modified</p>
              <p className="text-sm text-gray-900 mt-1">
                {selectedFile.modified}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* File preview windows */}
      {openedFiles.map((window, index) => {
        const isFocused = focusedWindowId === window.instanceId;
        const baseZIndex = 1000 + index;
        const zIndex = isFocused ? baseZIndex + openedFiles.length : baseZIndex;

        return (
          <Window
            key={window.instanceId}
            title={window.file.name}
            supportDark
            onClose={() => handleCloseFile(window.instanceId)}
            onFocus={() => setFocusedWindowId(window.instanceId)}
            zIndex={zIndex}
            initialX={200 + index * 30}
            initialY={150 + index * 30}
            width={800}
            height={600}
          >
            <div className="p-6">{getFileComponent(window.file.id)}</div>
          </Window>
        );
      })}

      {/* Password Dialog */}
      <PasswordDialog
        isOpen={passwordDialogOpen}
        title="Access Required"
        onSubmit={handlePasswordSubmit}
        onCancel={() => {
          setPasswordDialogOpen(false);
          setPendingFile(null);
          setPasswordError(false);
        }}
        isError={passwordError}
        hint={pendingFile?.hint}
      />
    </div>
  );
}
